import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertTriangle, Archive, ArrowLeft, CheckCircle2, ClipboardSignature, Download,
  FileSignature, GitBranch, GitCompare, History, Lock, Mail, Plus, RefreshCw, Save,
  ShieldCheck, Trash2, X, Stamp, BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { apiRequest, downloadFile } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  SIGNATURE_ROLE_LABELS,
  DATA_CERTA_LABELS,
  STATUS_LABELS,
  STATUS_CLASSES,
  CHANGE_CLASSES,
  CHANGE_LABELS,
  EMPTY_SIGNATURE as emptySignature,
} from "./DvrRevisions.constants";
import {
  StatusBadge,
  ChangedSectionsList,
  SignaturePanel as SignatureForm,
  CreateRevisionDialog,
} from "./DvrRevisions.components";


export default function DvrRevisions() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const SIGNATURE_ROLE_LABELS_I18N = useMemo(() => ({
    datore_lavoro: t("revisions.role_labels.datore_lavoro"),
    rspp: t("revisions.role_labels.rspp"),
    aspp: t("revisions.role_labels.aspp"),
    medico_competente: t("revisions.role_labels.medico_competente"),
    rls: t("revisions.role_labels.rls"),
    rlst: t("revisions.role_labels.rlst"),
  }), [t, i18n.language]);
  const DATA_CERTA_LABELS_I18N = useMemo(() => ({
    firma_digitale: t("revisions.method_labels.firma_digitale"),
    marca_temporale: t("revisions.method_labels.marca_temporale"),
    pec: t("revisions.method_labels.pec"),
    protocollo_interno: t("revisions.method_labels.protocollo_interno"),
    altro: t("revisions.method_labels.altro"),
  }), [t, i18n.language]);
  const STATUS_LABELS_I18N = useMemo(() => ({
    draft: t("revisions.status_labels.draft"),
    in_review: t("revisions.status_labels.in_review"),
    approved: t("revisions.status_labels.approved"),
    signed: t("revisions.status_labels.signed"),
    archived: t("revisions.status_labels.archived"),
  }), [t, i18n.language]);
  const CHANGE_LABELS_I18N = useMemo(() => ({
    added: t("revisions.change_labels.added"),
    modified: t("revisions.change_labels.modified"),
    removed: t("revisions.change_labels.removed"),
    unchanged: t("revisions.change_labels.unchanged"),
  }), [t, i18n.language]);
  const [company, setCompany] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const canWrite = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"].includes(user.role);
  const canSign = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP", "MEDICO_COMPETENTE", "RLS"].includes(user.role);
  const canFinalize = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN"].includes(user.role);

  const load = async () => {
    if (!id) return;
    try {
      const [companyData, revs] = await Promise.all([
        apiRequest(`/companies/${id}`),
        apiRequest(`/companies/${id}/dvr-revisions`),
      ]);
      setCompany(companyData);
      setRevisions(revs);
      if (!selectedId && revs.length > 0) setSelectedId(revs[0].id);
      if (selectedId && !revs.find((r) => r.id === selectedId)) setSelectedId(revs[0]?.id || null);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const selected = useMemo(
    () => revisions.find((r) => r.id === selectedId) || null,
    [revisions, selectedId]
  );

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      const rev = await apiRequest(`/companies/${id}/dvr-revisions`, {
        method: "POST", body: JSON.stringify(payload),
      });
      toast.success(`Revisione v${rev.revision_number} creata`);
      setCreateOpen(false);
      await load();
      setSelectedId(rev.id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSignature = async (sigPayload) => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}/signatures`, {
        method: "POST", body: JSON.stringify(sigPayload),
      });
      toast.success("Firma registrata");
      setSigOpen(false);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSignature = async (role) => {
    if (!selected || !window.confirm("Rimuovere la firma?")) return;
    try {
      await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}/signatures/${role}`, { method: "DELETE" });
      toast.success("Firma rimossa");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleFinalize = async () => {
    if (!selected || !window.confirm("Confermi la firma definitiva della revisione? Diventerà immutabile.")) return;
    try {
      await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}/sign`, { method: "POST" });
      toast.success("Revisione firmata e bloccata");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleArchive = async () => {
    if (!selected || !window.confirm("Archiviare la revisione?")) return;
    try {
      await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}/archive`, { method: "POST" });
      toast.success("Revisione archiviata");
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm("Eliminare definitivamente la revisione?")) return;
    try {
      await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}`, { method: "DELETE" });
      toast.success("Revisione eliminata");
      setSelectedId(null);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDownloadPdf = async () => {
    if (!selected) return;
    try {
      await downloadFile(
        `/companies/${id}/dvr-revisions/${selected.id}/pdf`,
        `DVR_rev_v${selected.revision_number}.pdf`,
      );
      toast.success("PDF scaricato");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleNotifySigners = async () => {
    if (!selected) return;
    try {
      const log = await apiRequest(`/companies/${id}/dvr-revisions/${selected.id}/notify-signers`, { method: "POST" });
      if (!log.smtp_configured) {
        toast.warning(`Notifiche pronte (SMTP non configurato). ${log.recipients_total} destinatari · scadenza ${log.deadline}`);
      } else {
        toast.success(`Notifiche inviate a ${log.recipients_sent}/${log.recipients_total} firmatari · scadenza ${log.deadline}`);
      }
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Iter27: QTSP qualified signature flow (sandbox + production)
  const [qtspOpen, setQtspOpen] = useState(false);
  const [qtspForm, setQtspForm] = useState({ signer_role: "datore_lavoro", signer_name: "", phone_number: "" });
  const [qtspRequestId, setQtspRequestId] = useState("");
  const [qtspOtp, setQtspOtp] = useState("");
  const [qtspMode, setQtspMode] = useState("sandbox");
  const [qtspBusy, setQtspBusy] = useState(false);

  const openQtsp = () => {
    setQtspForm({ signer_role: "datore_lavoro", signer_name: "", phone_number: "" });
    setQtspRequestId("");
    setQtspOtp("");
    setQtspOpen(true);
  };

  const submitQtspRequest = async (e) => {
    e?.preventDefault?.();
    if (!selected) return;
    setQtspBusy(true);
    try {
      const r = await apiRequest(
        `/companies/${id}/dvr-revisions/${selected.id}/qtsp/sign-request`,
        { method: "POST", body: JSON.stringify(qtspForm) },
      );
      setQtspRequestId(r.qtsp_request_id);
      setQtspMode(r.mode);
      toast.success(r.hint ? `${r.status} · ${r.hint}` : `OTP inviato via ${r.channel}`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setQtspBusy(false);
    }
  };

  const submitQtspOtp = async () => {
    if (!selected || !qtspRequestId) return;
    setQtspBusy(true);
    try {
      const r = await apiRequest(
        `/companies/${id}/dvr-revisions/${selected.id}/qtsp/confirm-otp`,
        {
          method: "POST",
          body: JSON.stringify({
            qtsp_request_id: qtspRequestId,
            otp_code: qtspOtp,
            data_certa_method: "firma_digitale",
          }),
        },
      );
      if (r.status === "completed") {
        toast.success(`Firma qualificata completata (${r.mode}) · firma registrata`);
        setQtspOpen(false);
        await load();
      } else if (r.status === "otp_failed") {
        toast.error(r.detail || "OTP non valido");
      } else {
        toast.message(`Stato QTSP: ${r.status}`);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setQtspBusy(false);
    }
  };

  // Notification history
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const loadHistory = async () => {
    setHistoryOpen(true);
    try {
      const [rows, stats] = await Promise.all([
        apiRequest(`/companies/${id}/notification-history`),
        apiRequest(`/companies/${id}/notification-history/stats`),
      ]);
      setHistoryRows(rows);
      setHistoryStats(stats);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const [diffOpen, setDiffOpen] = useState(false);
  const [diffLeftId, setDiffLeftId] = useState("");
  const [diffRightId, setDiffRightId] = useState("");
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffHideUnchanged, setDiffHideUnchanged] = useState(true);

  const openDiff = () => {
    if (revisions.length < 2) {
      toast.error("Servono almeno 2 revisioni per il confronto");
      return;
    }
    setDiffLeftId(revisions[revisions.length - 1].id);
    setDiffRightId(revisions[0].id);
    setDiffData(null);
    setDiffHideUnchanged(true);
    setDiffOpen(true);
  };

  const runDiff = async () => {
    if (!diffLeftId || !diffRightId) return;
    if (diffLeftId === diffRightId) {
      toast.error("Seleziona due revisioni diverse");
      return;
    }
    setDiffLoading(true);
    try {
      const data = await apiRequest(
        `/companies/${id}/dvr-revisions-diff?left=${diffLeftId}&right=${diffRightId}`,
      );
      setDiffData(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDiffLoading(false);
    }
  };

  if (!company) {
    return <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-revisions-loading">Caricamento azienda...</div>;
  }

  const locked = selected?.locked;
  const signedRoles = new Set((selected?.signatures || []).map((s) => s.role));
  const hasRequiredSignatures = signedRoles.has("datore_lavoro") && signedRoles.has("rspp");

  return (
    <div className="space-y-6" data-testid="dvr-revisions-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link
          to={`/aziende/${id}`}
          className="inline-flex items-center gap-2 text-sm font-semibold text-blue-900 hover:underline"
          data-testid="back-to-company-link"
        >
          <ArrowLeft className="h-4 w-4" /> Torna all'azienda
        </Link>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-revisions-button">
            <RefreshCw className="h-4 w-4" /> Aggiorna
          </Button>
          {canWrite && (
            <Button
              className="bg-blue-900 hover:bg-blue-800"
              onClick={() => setCreateOpen(true)}
              data-testid="open-create-revision-button"
            >
              <Plus className="h-4 w-4" /> {t("revisions.new_revision")}
            </Button>
          )}
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6" data-testid="dvr-revisions-header">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">{t("revisions.page_kicker")}</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">{t("revisions.page_title")} · {company.business_name}</h2>
        <p className="mt-2 text-slate-600">{t("revisions.page_subtitle")}</p>
      </section>

      {error && (
        <Alert variant="destructive" data-testid="dvr-revisions-error">
          <AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
        {/* Registro cronologico */}
        <Card className="shadow-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <History className="h-4 w-4" /> Registro revisioni ({revisions.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 p-3">
            {revisions.length === 0 && (
              <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500" data-testid="no-revisions-message">
                Nessuna revisione presente. Creane una per iniziare.
              </p>
            )}
            {revisions.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setSelectedId(r.id)}
                className={`w-full rounded-md border p-3 text-left text-sm transition ${selectedId === r.id ? "border-blue-900 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
                data-testid={`revision-item-${r.revision_number}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{t("revisions.revision_label")} v{r.revision_number}</span>
                  <StatusBadge status={r.status} label={STATUS_LABELS_I18N[r.status]} />
                </div>
                <div className="mt-1 text-xs text-slate-500">{r.revision_date}</div>
                <div className="mt-1 line-clamp-2 text-xs text-slate-700">{r.reason}</div>
                {r.locked && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                    <Lock className="h-3 w-3" /> immutabile
                  </div>
                )}
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Dettaglio */}
        <div className="space-y-4">
          {!selected && (
            <Card className="shadow-none"><CardContent className="p-8 text-center text-sm text-slate-500" data-testid="no-revision-selected">
              Seleziona una revisione dal registro o creane una nuova.
            </CardContent></Card>
          )}
          {selected && (
            <>
              <Card className="shadow-none" data-testid="revision-detail-card">
                <CardHeader className="flex flex-row items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <GitBranch className="h-5 w-5" /> {t("revisions.revision_label")} v{selected.revision_number} · {selected.revision_date}
                    </CardTitle>
                    <p className="mt-1 text-sm text-slate-600">{selected.reason}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={selected.status} label={STATUS_LABELS_I18N[selected.status]} />
                    {locked && <Badge className="bg-emerald-700 text-white"><Lock className="h-3 w-3 mr-1" /> Locked</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-3 text-sm">
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Data documento</Label>
                    <div data-testid="revision-document-date">{selected.document_date || "—"}</div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Creata da</Label>
                    <div>{selected.created_by_name || selected.created_by}</div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-slate-500">Approvata da</Label>
                    <div>{selected.approved_by_name || "—"}</div>
                  </div>
                  {selected.notes && (
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase text-slate-500">Note</Label>
                      <p className="whitespace-pre-wrap">{selected.notes}</p>
                    </div>
                  )}
                </CardContent>
                <CardContent className="border-t border-slate-100 pt-4">
                  <div className="flex flex-wrap gap-2">
                    {canSign && !locked && (
                      <Button onClick={() => setSigOpen(true)} data-testid="add-signature-button" className="bg-blue-900 hover:bg-blue-800">
                        <ClipboardSignature className="h-4 w-4" /> Aggiungi firma
                      </Button>
                    )}
                    {canFinalize && !locked && (
                      <Button
                        onClick={handleFinalize}
                        disabled={!hasRequiredSignatures}
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid="finalize-revision-button"
                        title={!hasRequiredSignatures ? "Servono almeno le firme di Datore di lavoro e RSPP" : ""}
                      >
                        <ShieldCheck className="h-4 w-4" /> Firma e blocca
                      </Button>
                    )}
                    <Button variant="outline" onClick={handleDownloadPdf} data-testid="export-revision-pdf-button">
                      <Download className="h-4 w-4" /> Scarica PDF
                    </Button>
                    <Button variant="outline" onClick={openQtsp} data-testid="qtsp-sign-button">
                      <Stamp className="h-4 w-4" /> Firma qualificata QTSP
                    </Button>
                    {canWrite && (
                      <Button variant="outline" onClick={handleNotifySigners} data-testid="notify-signers-button">
                        <Mail className="h-4 w-4" /> Notifica firmatari (PEC)
                      </Button>
                    )}
                    <Button variant="outline" onClick={loadHistory} data-testid="open-notification-history-button">
                      <BellRing className="h-4 w-4" /> Storico notifiche
                    </Button>
                    <Button
                      variant="outline"
                      onClick={openDiff}
                      disabled={revisions.length < 2}
                      data-testid="open-diff-revisions-button"
                    >
                      <GitCompare className="h-4 w-4" /> Confronta revisioni
                    </Button>
                    {canFinalize && selected.status !== "archived" && (
                      <Button variant="outline" onClick={handleArchive} data-testid="archive-revision-button">
                        <Archive className="h-4 w-4" /> Archivia
                      </Button>
                    )}
                    {canFinalize && !locked && (
                      <Button variant="outline" onClick={handleDelete} className="text-red-700" data-testid="delete-revision-button">
                        <Trash2 className="h-4 w-4" /> Elimina
                      </Button>
                    )}
                  </div>
                  {selected.signature_deadline && !locked && (
                    <p className="mt-3 text-sm text-blue-900" data-testid="signature-deadline-banner">
                      <Mail className="inline h-4 w-4 mr-1" />
                      Scadenza firma con data certa: <strong>{selected.signature_deadline}</strong>
                      {selected.notification_log && (
                        <> · notifiche inviate a {selected.notification_log.recipients_sent}/{selected.notification_log.recipients_total} firmatari</>
                      )}
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Sezione Firme e data certa */}
              <Card className="shadow-none" data-testid="signatures-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <FileSignature className="h-4 w-4" /> Firme e data certa ({(selected.signatures || []).length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(!selected.signatures || selected.signatures.length === 0) && (
                    <p className="rounded-md border border-dashed border-slate-300 p-4 text-center text-sm text-slate-500" data-testid="no-signatures-message">
                      Nessuna firma registrata. Aggiungi quelle di Datore di lavoro, RSPP, Medico competente e RLS/RLST.
                    </p>
                  )}
                  {(selected.signatures || []).map((s) => (
                    <div
                      key={s.role}
                      className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-slate-200 bg-white p-3"
                      data-testid={`signature-row-${s.role}`}
                    >
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-blue-900 text-white">{SIGNATURE_ROLE_LABELS_I18N[s.role] || SIGNATURE_ROLE_LABELS[s.role] || s.role}</Badge>
                          {s.consultation_only && <Badge variant="outline">{t("revisions.consultative_note").replace("RLS/RLST:", "").trim()}</Badge>}
                        </div>
                        <div className="font-semibold">{s.signer_name} {s.fiscal_code && <span className="text-xs text-slate-500">· {s.fiscal_code}</span>}</div>
                        <div className="text-xs text-slate-600">
                          {t("revisions.signature_dialog_title").includes("Add") ? "Signed on" : "Firmato il"} {s.signed_at ? s.signed_at.slice(0, 10) : "—"}
                          {s.data_certa_method && <> · <strong>{DATA_CERTA_LABELS_I18N[s.data_certa_method] || DATA_CERTA_LABELS[s.data_certa_method] || s.data_certa_method}</strong></>}
                          {s.data_certa_date && <> · {t("revisions.data_certa_date").toLowerCase()} {s.data_certa_date}</>}
                        </div>
                        {s.data_certa_reference && (
                          <div className="text-xs text-slate-500">Rif. {s.data_certa_reference}</div>
                        )}
                        {s.notes && <div className="text-xs text-slate-500">{s.notes}</div>}
                      </div>
                      {canWrite && !locked && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSignature(s.role)}
                          className="text-red-700"
                          data-testid={`remove-signature-${s.role}-button`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {!hasRequiredSignatures && !locked && (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertTitle>Firme obbligatorie mancanti</AlertTitle>
                      <AlertDescription>
                        Per firmare la revisione servono almeno: Datore di lavoro e RSPP.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Sezioni modificate */}
              <Card className="shadow-none" data-testid="changes-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CheckCircle2 className="h-4 w-4" /> Sezioni modificate rispetto alla revisione precedente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ChangedSectionsList items={selected.changed_sections || []} />
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      <CreateRevisionDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreate={handleCreate}
        saving={saving}
      />
      <SignatureForm
        open={sigOpen}
        onClose={() => setSigOpen(false)}
        onSave={handleSignature}
        saving={saving}
      />
      <Dialog open={diffOpen} onOpenChange={(o) => { if (!o) setDiffOpen(false); }}>
        <DialogContent className="max-w-5xl" data-testid="diff-revisions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5" /> Confronta revisioni DVR
            </DialogTitle>
            <DialogDescription>
              Confronto side-by-side dei dati aziendali, dei conteggi e dei livelli di rischio tra due revisioni.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Revisione sinistra</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={diffLeftId}
                onChange={(e) => setDiffLeftId(e.target.value)}
                data-testid="diff-left-select"
              >
                {revisions.map((r) => (
                  <option key={r.id} value={r.id}>v{r.revision_number} · {r.status} · {r.revision_date}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Revisione destra</Label>
              <select
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                value={diffRightId}
                onChange={(e) => setDiffRightId(e.target.value)}
                data-testid="diff-right-select"
              >
                {revisions.map((r) => (
                  <option key={r.id} value={r.id}>v{r.revision_number} · {r.status} · {r.revision_date}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={runDiff} disabled={diffLoading} className="bg-blue-900 hover:bg-blue-800" data-testid="run-diff-button">
                {diffLoading ? "Calcolo..." : "Confronta"}
              </Button>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={diffHideUnchanged}
                  onChange={(e) => setDiffHideUnchanged(e.target.checked)}
                  data-testid="diff-hide-unchanged-checkbox"
                /> Nascondi invariati
              </label>
            </div>
          </div>
          {diffData && (
            <div className="mt-2 space-y-2" data-testid="diff-result">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge className="bg-emerald-50 text-emerald-700" data-testid="diff-summary-added">+{diffData.summary.added} aggiunti</Badge>
                <Badge className="bg-amber-50 text-amber-800" data-testid="diff-summary-modified">~{diffData.summary.modified} modificati</Badge>
                <Badge className="bg-red-50 text-red-700" data-testid="diff-summary-removed">-{diffData.summary.removed} rimossi</Badge>
                <Badge className="bg-slate-100 text-slate-600" data-testid="diff-summary-unchanged">={diffData.summary.unchanged} invariati</Badge>
              </div>
              <div className="max-h-96 overflow-auto rounded-md border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="px-2 py-1 text-left">Sezione</th>
                      <th className="px-2 py-1 text-left">Campo</th>
                      <th className="px-2 py-1 text-left">v{diffData.left.revision_number}</th>
                      <th className="px-2 py-1 text-left">v{diffData.right.revision_number}</th>
                      <th className="px-2 py-1 text-left">Cambiamento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diffData.rows
                      .filter((r) => !diffHideUnchanged || r.change_type !== "unchanged")
                      .map((r, idx) => (
                        <tr
                          key={idx}
                          className={CHANGE_CLASSES[r.change_type] || ""}
                          data-testid={`diff-row-${idx}`}
                        >
                          <td className="px-2 py-1 font-semibold">{r.section}</td>
                          <td className="px-2 py-1">{r.key}</td>
                          <td className="px-2 py-1">{r.left_value || "—"}</td>
                          <td className="px-2 py-1">{r.right_value || "—"}</td>
                          <td className="px-2 py-1 uppercase text-xs">{CHANGE_LABELS[r.change_type]}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)} data-testid="diff-close-button">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QTSP Aruba sign dialog */}
      <Dialog open={qtspOpen} onOpenChange={(o) => { if (!o) setQtspOpen(false); }}>
        <DialogContent className="max-w-xl" data-testid="qtsp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stamp className="h-5 w-5" /> Firma qualificata remota (Aruba QTSP)
            </DialogTitle>
            <DialogDescription>
              Modalità: <strong>{qtspMode}</strong>. {qtspMode === "sandbox" && "In sandbox usa OTP '123456' per testare il flusso."}
            </DialogDescription>
          </DialogHeader>
          {!qtspRequestId && (
            <form onSubmit={submitQtspRequest} className="space-y-3">
              <div className="space-y-2">
                <Label>Ruolo firmatario</Label>
                <select
                  className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                  value={qtspForm.signer_role}
                  onChange={(e) => setQtspForm((p) => ({ ...p, signer_role: e.target.value }))}
                  data-testid="qtsp-role-select"
                >
                  <option value="datore_lavoro">Datore di lavoro</option>
                  <option value="rspp">RSPP</option>
                  <option value="aspp">ASPP</option>
                  <option value="medico_competente">Medico competente</option>
                  <option value="rls">RLS</option>
                  <option value="rlst">RLST</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Nome firmatario</Label>
                <Input required value={qtspForm.signer_name} onChange={(e) => setQtspForm((p) => ({ ...p, signer_name: e.target.value }))} data-testid="qtsp-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Numero di telefono (per OTP)</Label>
                <Input required placeholder="+393331234567" value={qtspForm.phone_number} onChange={(e) => setQtspForm((p) => ({ ...p, phone_number: e.target.value }))} data-testid="qtsp-phone-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setQtspOpen(false)} data-testid="qtsp-cancel-button">Annulla</Button>
                <Button type="submit" disabled={qtspBusy} className="bg-blue-900 hover:bg-blue-800" data-testid="qtsp-submit-request-button">
                  {qtspBusy ? "Invio..." : "Avvia firma"}
                </Button>
              </DialogFooter>
            </form>
          )}
          {qtspRequestId && (
            <div className="space-y-3">
              <Alert>
                <AlertTitle>Richiesta inviata</AlertTitle>
                <AlertDescription>
                  Codice richiesta: <code className="font-mono text-xs">{qtspRequestId}</code>
                </AlertDescription>
              </Alert>
              <div className="space-y-2">
                <Label>Inserisci OTP ricevuto</Label>
                <Input
                  inputMode="numeric"
                  value={qtspOtp}
                  onChange={(e) => setQtspOtp(e.target.value)}
                  placeholder={qtspMode === "sandbox" ? "123456" : "OTP via SMS"}
                  data-testid="qtsp-otp-input"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setQtspOpen(false)} data-testid="qtsp-close-button">Annulla</Button>
                <Button onClick={submitQtspOtp} disabled={qtspBusy || qtspOtp.length < 4} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="qtsp-confirm-otp-button">
                  {qtspBusy ? "Verifica..." : "Conferma firma"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notification history dialog */}
      <Dialog open={historyOpen} onOpenChange={(o) => { if (!o) setHistoryOpen(false); }}>
        <DialogContent className="max-w-5xl" data-testid="notification-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BellRing className="h-5 w-5" /> Storico notifiche · {company.business_name}
            </DialogTitle>
            <DialogDescription>Cronologia delle notifiche PEC/email inviate ai firmatari (creazione, reminder, escalation, manuali).</DialogDescription>
          </DialogHeader>
          {historyStats && (
            <div className="flex flex-wrap gap-2 text-xs" data-testid="history-stats">
              <Badge className="bg-blue-50 text-blue-900">Totali: {historyStats.total}</Badge>
              <Badge className="bg-emerald-50 text-emerald-700">Consegnate: {historyStats.delivered}</Badge>
              <Badge className="bg-red-50 text-red-700">Fallite: {historyStats.failed}</Badge>
              {Object.entries(historyStats.by_event || {}).map(([k, v]) => (
                <Badge key={k} variant="outline">{k}: {v.total}</Badge>
              ))}
            </div>
          )}
          <div className="max-h-[420px] overflow-auto rounded-md border border-slate-200" data-testid="history-table-wrapper">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-100">
                <tr>
                  <th className="px-2 py-1 text-left">Data</th>
                  <th className="px-2 py-1 text-left">Evento</th>
                  <th className="px-2 py-1 text-left">Ruolo</th>
                  <th className="px-2 py-1 text-left">Destinatario</th>
                  <th className="px-2 py-1 text-left">Rev.</th>
                  <th className="px-2 py-1 text-left">Esito</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.length === 0 && (
                  <tr><td colSpan={6} className="px-2 py-3 text-center text-slate-500" data-testid="history-empty">Nessuna notifica registrata.</td></tr>
                )}
                {historyRows.map((h, idx) => (
                  <tr key={h.id} className={h.delivered ? "" : "bg-red-50"} data-testid={`history-row-${idx}`}>
                    <td className="px-2 py-1">{h.created_at?.slice(0, 19).replace("T", " ")}</td>
                    <td className="px-2 py-1">{h.event_type}</td>
                    <td className="px-2 py-1">{h.recipient_role}</td>
                    <td className="px-2 py-1">{h.recipient_name} <span className="text-slate-400">{h.recipient_email}</span></td>
                    <td className="px-2 py-1">v{h.revision_number || "—"}</td>
                    <td className="px-2 py-1">{h.delivered ? "✔" : "✘"} <span className="text-slate-400">{h.detail || ""}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} data-testid="history-close-button">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
